/**
 * Evaro deployment environment. Consumers pass `env` instead of hardcoding
 * URLs — the env-to-URL mapping lives here and nowhere else, so a CDN domain
 * change never has to reach a vendor's site.
 *
 * `development` / `production` are aliases for `dev` / `prod`, and `preview`
 * (Vercel preview deploys) resolves to `staging` — so a value like Vercel's
 * `VERCEL_ENV` can be passed straight through.
 */
export type EvaroEnv = 'dev' | 'development' | 'staging' | 'preview' | 'prod' | 'production'

/** Canonical env keys — every alias above normalizes onto one of these three. */
type CanonicalEnv = 'dev' | 'staging' | 'prod'

const LANDINGS_CDN_BY_ENV: Record<CanonicalEnv, string> = {
  prod: 'https://vendor.evaro.com',
  staging: 'https://vendor-staging.evaro.com',
  dev: 'https://vendor-development.evaro.com',
}

const API_BASE_BY_ENV: Record<CanonicalEnv, string> = {
  prod: 'https://api.evaro.com',
  staging: 'https://api-staging.evaro.com',
  dev: 'https://api-development.evaro.com',
}

const CONNECT_IFRAME_BY_ENV: Record<CanonicalEnv, string> = {
  prod: 'https://connect.evaro.com/iframe.js',
  staging: 'https://connect-staging.evaro.com/iframe.js',
  dev: 'https://connect-development.evaro.com/iframe.js',
}

/** Production landings CDN — the fallback when no `env` / `cdn` is given. */
export const DEFAULT_LANDINGS_CDN = LANDINGS_CDN_BY_ENV.prod

/** Production Connect launcher — the fallback when no `env` / `src` is given. */
export const DEFAULT_CONNECT_IFRAME_URL = CONNECT_IFRAME_BY_ENV.prod

/**
 * API base inferred from the host page's hostname. Used only as the last-resort
 * fallback when neither an explicit `apiBase` nor an `env` is supplied — a new
 * integration should pass `env` and not rely on this.
 */
export const DEFAULT_API_BASE = (() => {
  if (typeof window === 'undefined') return API_BASE_BY_ENV.prod
  const host = window.location?.hostname || ''
  if (host.includes('staging')) return API_BASE_BY_ENV.staging
  if (host.includes('development') || host.includes('localhost') || host === '127.0.0.1') {
    return API_BASE_BY_ENV.dev
  }
  return API_BASE_BY_ENV.prod
})()

/**
 * Coerce an arbitrary string to a canonical env, or `null` if it isn't one.
 * `development` / `production` alias `dev` / `prod`; `preview` resolves to
 * `staging`.
 */
export function normalizeEnv(env: string | null | undefined): CanonicalEnv | null {
  switch (env) {
    case 'dev':
    case 'development':
      return 'dev'
    case 'staging':
    case 'preview':
      return 'staging'
    case 'prod':
    case 'production':
      return 'prod'
    default:
      return null
  }
}

/**
 * Origin `loader.js` was served from. The loader sets this on startup so the
 * landings CDN defaults to wherever the loader itself came from — loader and
 * manifests are always co-deployed. Stays `null` for npm consumers (no loader).
 */
let loaderOrigin: string | null = null

/** Record the origin `loader.js` was loaded from. Called once, by the loader. */
export function setLoaderOrigin(origin: string | null | undefined): void {
  loaderOrigin = origin || null
}

/**
 * Resolve the landings CDN origin.
 * Precedence: explicit `cdn` → `env` mapping → loader's own origin → prod default.
 */
export function resolveLandingsCdn(opts: { cdn?: string | null; env?: string | null } = {}): string {
  if (opts.cdn) return opts.cdn
  const env = normalizeEnv(opts.env)
  if (env) return LANDINGS_CDN_BY_ENV[env]
  return loaderOrigin ?? DEFAULT_LANDINGS_CDN
}

/**
 * Resolve the Evaro API base.
 * Precedence: explicit `apiBase` override → `env` mapping → hostname default.
 */
export function resolveApiBase(opts: { apiBase?: string | null; env?: string | null } = {}): string {
  if (opts.apiBase) return opts.apiBase
  const env = normalizeEnv(opts.env)
  return env ? API_BASE_BY_ENV[env] : DEFAULT_API_BASE
}

/**
 * Resolve the Connect launcher (`iframe.js`) URL.
 * Precedence: explicit `src` override → `env` mapping → production default.
 */
export function resolveConnectIframeUrl(opts: { src?: string | null; env?: string | null } = {}): string {
  if (opts.src) return opts.src
  const env = normalizeEnv(opts.env)
  return env ? CONNECT_IFRAME_BY_ENV[env] : DEFAULT_CONNECT_IFRAME_URL
}
